import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { LockOutlined, CloseOutlined, ReloadOutlined, CrownOutlined, ZoomInOutlined, ZoomOutOutlined, UndoOutlined, LeftOutlined } from '@ant-design/icons';
import { Button, Tooltip } from 'antd';
import { useIntl } from 'react-intl';
import { IBrandEquityScoreResponse } from '../../services/nestjs-base-api';
import ReactMarkdown from 'react-markdown';
import { getDisplayName } from '../../utils/elementDisplayNames';

interface ElementDetails {
    inputs: Array<{ name: string; type: string; score: number | null | undefined }>;
    outputs: Array<{ name: string; type: string; score: number | null | undefined }>;
    score: number | null | undefined;
    aiSummary?: string;
    statements?: Array<{ id: string; text: string; meanScore: number; responseCount: number }>;
    type: 'top' | 'latent' | 'qc';
    locked: boolean;
    classificationLabel?: string;
}

interface BrandEquityVisualizationV3Props {
    results: IBrandEquityScoreResponse;
    isModal?: boolean;
    onElementClick?: (elementName: string, elementData: any) => void;
    elementDescriptions?: Record<string, { mouseover?: string; sidebar?: string }>;
    focusedElementName?: string | null;
    initialSelectedElement?: string | null;
    getElementDetails?: (elementName: string) => ElementDetails;
    onReanalyze?: (elementName: string) => Promise<string | undefined>;
    isReanalyzing?: boolean;
    onUpgradeClick?: () => void;
    onStatementClick?: (statement: any, elementName: string) => void;
}

interface Node {
    element: string;
    score: number | null;
    ring: number; // 0 = center, 1-4 = orbital rings
    x: number;
    y: number;
    targetX: number;
    targetY: number;
    radius: number;
    color: string;
    angle: number;
    hovered: boolean;
    locked: boolean;
    type: 'brandEquity' | 'latent' | 'measured';
    colorGroup: 'amber' | 'green' | 'orange' | 'purple' | 'red' | 'blue' | 'grey';
}

interface Connection {
    from: string;
    to: string;
}

interface Config {
    zoom: number;
}

interface PanState {
    offsetX: number;
    offsetY: number;
    isDragging: boolean;
    hasMoved: boolean;
    lastX: number;
    lastY: number;
}

// Color configuration for the 5 color groups
const COLORS = {
    amber: '#3d5a80',   // Brand Equity (center) - dark steel blue
    green: '#3d5a80',   // Integral: Brand Loyalty, Perceived Quality (Ring 1) - dark steel blue
    orange: '#8fb8d4',  // Transitional: Behavioural Loyalty, Associations, Attitudinal Loyalty (Ring 2) - light powder blue
    purple: '#b39ddb', // Market Awareness - pastel purple
    red: '#e07a5f',     // Fundamental: Alignment Depth, Brand Attachment, PQV (Ring 3) - coral/terracotta
    blue: '#1b2a41',    // Measured: All 25 Measured Elements (Ring 4) - very dark navy
    grey: '#9ca3af',    // Locked elements - muted grey
};

// Ring radii configuration
const RING_RADII = [0, 140, 230, 310, 420]; // Center, Ring1, Ring2, Ring3, Ring4

// Element sizes (radius) for each ring level
const ELEMENT_SIZES = [60, 35, 28, 25, 22]; // Ring 0, 1, 2, 3, 4

// Element assignments to rings, color groups, and specific angles (in clock positions)
// Using 4 positions: 1:30, 4:30, 7:30, 10:30
const clockToRadians = (hour: number, minutes: number = 0): number => {
    const totalMinutes = hour * 60 + minutes;
    const degrees = (totalMinutes / 720) * 360 - 90; // 720 minutes = full circle, -90 to start at 12
    return (degrees * Math.PI) / 180;
};

const ELEMENT_CONFIG: Record<string, { ring: number; colorGroup: 'amber' | 'green' | 'orange' | 'purple' | 'red' | 'blue'; angle?: number; labelOffsetX?: number; labelOffsetY?: number }> = {
    'Brand Equity': { ring: 0, colorGroup: 'amber' },
    // Ring 1 - Green (2 elements)
    'Brand Loyalty': { ring: 1, colorGroup: 'green', angle: clockToRadians(9, 0) },
    'Perceived Quality': { ring: 1, colorGroup: 'green', angle: clockToRadians(3, 0) },
    // Ring 2 - Orange/Light Blue (4 elements)
    'Behavioural Loyalty': { ring: 2, colorGroup: 'orange', angle: clockToRadians(1, 30) },
    'Willingness to Pay a Premium': { ring: 2, colorGroup: 'orange', angle: clockToRadians(4, 30) },
    'Associations': { ring: 2, colorGroup: 'orange', angle: clockToRadians(7, 30) },
    'Attitudinal Loyalty': { ring: 2, colorGroup: 'orange', angle: clockToRadians(10, 30) },
    // Ring 3 - Red (3 elements)
    'Alignment Depth': { ring: 3, colorGroup: 'red', angle: clockToRadians(12, 0) },
    'Brand Attachment': { ring: 3, colorGroup: 'red', angle: clockToRadians(3, 0) },
    'Quality Value Tradeoff (PQV)': { ring: 3, colorGroup: 'red', angle: clockToRadians(9, 0) },
    // Market Awareness - conditional element (only shows when data exists)
    'Market Awareness': { ring: 2, colorGroup: 'purple', angle: clockToRadians(6, 0) },
};

// Zoom constraints
const MIN_ZOOM = 0.3;
const MAX_ZOOM = 2.5;
const ZOOM_STEP = 0.1;
// Default zoom (90%) — the initial level (when nothing is saved) and where
// "Reset view" returns to. The last selected zoom is persisted to localStorage.
const DEFAULT_ZOOM = 0.9;

const BrandEquityVisualizationV3: React.FC<BrandEquityVisualizationV3Props> = ({
    results,
    isModal = false,
    onElementClick,
    elementDescriptions = {},
    focusedElementName = null,
    initialSelectedElement = null,
    getElementDetails,
    onReanalyze,
    isReanalyzing = false,
    onUpgradeClick,
    onStatementClick
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const nodesRef = useRef<Node[]>([]);
    const [hoveredNode, setHoveredNode] = useState<Node | null>(null);
    const animationFrameRef = useRef<number>();
    const lastTimeRef = useRef<number>(Date.now());
    const intl = useIntl();

    // LocalStorage key for preferences
    const PREFS_KEY = 'brandEquityV3Preferences';

    // Load saved preferences from localStorage
    const loadPreferences = useCallback(() => {
        try {
            const saved = localStorage.getItem(PREFS_KEY);
            if (saved) {
                return JSON.parse(saved);
            }
        } catch (e) {
            console.error('Failed to load V3 preferences', e);
        }
        return null;
    }, []);

    // Save preferences to localStorage
    const savePreferences = useCallback((prefs: { zoom?: number }) => {
        try {
            const current = loadPreferences() || {};
            const updated = { ...current, ...prefs };
            localStorage.setItem(PREFS_KEY, JSON.stringify(updated));
        } catch (e) {
            console.error('Failed to save V3 preferences', e);
        }
    }, [loadPreferences]);

    // Initialize state from saved preferences
    const savedPrefs = loadPreferences();
    const [config, setConfig] = useState<Config>({ zoom: savedPrefs?.zoom ?? DEFAULT_ZOOM });

    // Panel mode state
    const [selectedElement, setSelectedElement] = useState<string | null>(null);
    const [selectedElementDetails, setSelectedElementDetails] = useState<ElementDetails | null>(null);

    // Navigation history for panel mode
    const [navigationHistory, setNavigationHistory] = useState<Array<{ element: string; details: ElementDetails }>>([]);
    const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1);

    // Auto-select element from prop (e.g. navigated from building blocks)
    const initialSelectedRef = useRef(false);
    useEffect(() => {
        if (initialSelectedElement && getElementDetails && !initialSelectedRef.current) {
            const details = getElementDetails(initialSelectedElement);
            if (details) {
                setSelectedElement(initialSelectedElement);
                setSelectedElementDetails(details);
                initialSelectedRef.current = true;
            }
        }
    }, [initialSelectedElement, getElementDetails]);

    // Element AI summaries can arrive AFTER a panel is already open: The Detail
    // runs a second, slower pass for them, so on a cold cache the panel is
    // opened during the wait. selectedElementDetails is snapshotted on click, so
    // without this it keeps showing the no-summary fallback until the user
    // closes and reopens the element. Only ever fills a gap — never replaces a
    // summary already on screen, so an "Analyse Again" result is not clobbered.
    useEffect(() => {
        if (!selectedElement || !getElementDetails) return;
        if (selectedElementDetails?.aiSummary) return;
        const fresh = getElementDetails(selectedElement);
        if (fresh?.aiSummary) {
            setSelectedElementDetails((prev) =>
                prev ? { ...prev, aiSummary: fresh.aiSummary } : fresh,
            );
        }
        // results is the trigger: it changes identity when the AI pass lands.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [results, selectedElement, selectedElementDetails?.aiSummary, getElementDetails]);

    // Always use light theme and panels view mode
    const theme = (() => 'light' as 'dark' | 'light')();
    const viewMode = (() => 'panels' as 'drawer' | 'panels')();

    // Pan state
    const [pan, setPan] = useState<PanState>({
        offsetX: 0,
        offsetY: 0,
        isDragging: false,
        hasMoved: false,
        lastX: 0,
        lastY: 0
    });

    // Save zoom when it changes
    useEffect(() => {
        savePreferences({ zoom: config.zoom });
    }, [config.zoom, savePreferences]);

    // Theme colors
    const themeColors = useMemo(() => ({
        background: theme === 'dark' ? '#1a1f2e' : '#fafafa',
        textColor: theme === 'dark' ? '#ffffff' : '#1f2937',
        subtextColor: theme === 'dark' ? '#94a3b8' : '#64748b',
    }), [theme]);

    // Find focused node from focusedElementName
    const focusedNode = useMemo(() => {
        if (!focusedElementName) return null;
        return nodesRef.current.find(node => node.element === focusedElementName) || null;
    }, [focusedElementName]);

    // Build scores map from results
    const scoresMap = useMemo(() => {
        const map: Record<string, number> = {};
        map['Brand Equity'] = results.overallBrandEquityScore ?? 0;
        results.latentElementScores.forEach(le => {
            map[le.element] = le.score ?? 0;
        });
        results.questionCategoryScores.forEach(qc => {
            map[qc.category] = qc.meanScore ?? 0;
        });
        return map;
    }, [results]);

    // Helper function to check if an element is locked
    const isElementLocked = useCallback((elementName: string): boolean => {
        const latentElement = results.latentElementScores.find(le => le.element === elementName);
        if (latentElement) return latentElement.locked === true;
        const qcElement = results.questionCategoryScores.find(qc => qc.category === elementName);
        if (qcElement) return qcElement.locked === true;
        return false;
    }, [results]);

    // Extract connections from results
    const connectionData = useMemo(() => {
        const connections: Connection[] = [];
        results.latentElementScores.forEach(le => {
            if (le.inputs && le.inputs.length > 0) {
                le.inputs.forEach(input => {
                    connections.push({
                        from: input.name,
                        to: le.element
                    });
                });
            }
        });
        return connections;
    }, [results]);

    // Convert results data to node format with orbital positioning
    const brandEquityData = useMemo(() => {
        const data: Array<{ element: string; score: number | null; ring: number; colorGroup: 'amber' | 'green' | 'orange' | 'purple' | 'red' | 'blue' }> = [];

        // Add Brand Equity (center)
        data.push({
            element: 'Brand Equity',
            score: scoresMap['Brand Equity'] || 0,
            ring: 0,
            colorGroup: 'amber'
        });

        // Add latent elements by ring
        const latentElements = results.latentElementScores.map(le => le.element);
        latentElements.forEach(element => {
            const config = ELEMENT_CONFIG[element];
            if (config) {
                const leData = results.latentElementScores.find(le => le.element === element);
                data.push({
                    element,
                    score: leData ? leData.score : (scoresMap[element] ?? null),
                    ring: config.ring,
                    colorGroup: config.colorGroup
                });
            }
        });

        // Add measured elements (Ring 4 - Blue) - sort by display name for correct positioning
        const measuredElements = results.questionCategoryScores.map(qc => qc.category)
            .sort((a, b) => getDisplayName(a).localeCompare(getDisplayName(b)));
        measuredElements.forEach(element => {
            if (!ELEMENT_CONFIG[element]) {
                data.push({
                    element,
                    score: scoresMap[element] || 0,
                    ring: 4,
                    colorGroup: 'blue'
                });
            }
        });

        return data;
    }, [scoresMap, results]);

    // Initialize nodes
    useEffect(() => {
        if (!canvasRef.current) return;

        const canvas = canvasRef.current;
        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;

        // Count elements per ring for even distribution
        const ringCounts: Record<number, number> = {};
        brandEquityData.forEach(data => {
            ringCounts[data.ring] = (ringCounts[data.ring] || 0) + 1;
        });

        const ringIndices: Record<number, number> = {};
        const newNodes: Node[] = [];

        brandEquityData.forEach(data => {
            const ring = data.ring;
            ringIndices[ring] = ringIndices[ring] || 0;

            const locked = isElementLocked(data.element);
            const baseRadius = RING_RADII[ring] || RING_RADII[4];
            const elementRadius = ELEMENT_SIZES[ring] || ELEMENT_SIZES[4];
            const color = locked ? COLORS.grey : COLORS[data.colorGroup];

            // Calculate angle - use manual position if specified, otherwise distribute evenly
            let angle = 0;
            const elementConfig = ELEMENT_CONFIG[data.element];
            if (ring > 0) {
                if (elementConfig?.angle !== undefined) {
                    // Use manually specified angle for latent elements
                    angle = elementConfig.angle;
                } else {
                    // For measured elements (ring 4), distribute evenly with slight offset
                    const total = ringCounts[ring] || 1;
                    const index = ringIndices[ring];
                    // Start at 12:30 and distribute evenly
                    angle = (-Math.PI / 2) + (Math.PI / 12) + (index / total) * Math.PI * 2;
                }
            }

            const effectiveRadius = baseRadius * config.zoom;
            const x = ring === 0 ? centerX : centerX + Math.cos(angle) * effectiveRadius;
            const y = ring === 0 ? centerY : centerY + Math.sin(angle) * effectiveRadius;

            const node: Node = {
                element: data.element,
                score: data.score,
                ring: data.ring,
                x,
                y,
                targetX: x,
                targetY: y,
                radius: elementRadius,
                color,
                angle,
                hovered: false,
                locked,
                type: data.ring === 0 ? 'brandEquity' : (data.ring === 4 ? 'measured' : 'latent'),
                colorGroup: locked ? 'grey' : data.colorGroup
            };

            newNodes.push(node);
            ringIndices[ring]++;
        });

        nodesRef.current = newNodes;
    }, [brandEquityData, config.zoom, isElementLocked]);

    // Update node positions
    const updateNodes = useCallback(() => {
        if (!canvasRef.current) return;

        const canvas = canvasRef.current;
        const centerX = canvas.width / 2 + pan.offsetX;
        const centerY = canvas.height / 2 + pan.offsetY;

        nodesRef.current = nodesRef.current.map(node => {
            const effectiveRadius = RING_RADII[node.ring] * config.zoom;
            const newTargetX = node.ring === 0 ? centerX : centerX + Math.cos(node.angle) * effectiveRadius;
            const newTargetY = node.ring === 0 ? centerY : centerY + Math.sin(node.angle) * effectiveRadius;

            // Smooth interpolation
            const newX = node.x + (newTargetX - node.x) * 0.1;
            const newY = node.y + (newTargetY - node.y) * 0.1;

            return {
                ...node,
                x: newX,
                y: newY,
                targetX: newTargetX,
                targetY: newTargetY
            };
        });
    }, [config.zoom, pan.offsetX, pan.offsetY]);

    // Draw orbital rings (dashed circles)
    const drawOrbitalRings = useCallback((ctx: CanvasRenderingContext2D) => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const centerX = canvas.width / 2 + pan.offsetX;
        const centerY = canvas.height / 2 + pan.offsetY;

        ctx.setLineDash([6, 4]);
        ctx.lineWidth = 1.5;

        // Draw rings 1-4 with fading opacity (inner more visible, outer more faded)
        const ringOpacities = [0, 0.08, 0.07, 0.06, 0.04];
        for (let i = 1; i <= 4; i++) {
            const radius = RING_RADII[i] * config.zoom;
            ctx.strokeStyle = `rgba(0, 0, 0, ${ringOpacities[i]})`;
            ctx.beginPath();
            ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
            ctx.stroke();
        }
        ctx.setLineDash([]);
    }, [config.zoom, pan.offsetX, pan.offsetY]);

    // Connection line colors
    const connectionColors = useMemo(() => ({
        // "Input from" - things that feed INTO the hovered element (dark steel blue, solid)
        inputFrom: theme === 'dark' ? 'rgba(61, 90, 128, 0.8)' : 'rgba(61, 90, 128, 0.6)',
        // "Output to" - things the hovered element feeds INTO (coral, dashed)
        feedsInto: theme === 'dark' ? 'rgba(224, 122, 95, 0.85)' : 'rgba(224, 122, 95, 0.7)',
    }), [theme]);

    // Draw connection lines on hover/focus/selection
    const drawConnections = useCallback((ctx: CanvasRenderingContext2D) => {
        // In panel mode, also show connections for selected element
        const selectedNode = selectedElement ? nodesRef.current.find(n => n.element === selectedElement) : null;
        const activeNode = hoveredNode || focusedNode || selectedNode;
        if (!activeNode) return;

        connectionData.forEach(conn => {
            const fromNode = nodesRef.current.find(n => n.element === conn.from);
            const toNode = nodesRef.current.find(n => n.element === conn.to);

            if (!fromNode || !toNode) return;

            // Check connection type relative to active node
            // conn.from feeds INTO conn.to
            const isInputToActive = toNode.element === activeNode.element; // Something feeds INTO active
            const isFeedsFromActive = fromNode.element === activeNode.element; // Active feeds INTO something

            // Only show connections involving the active node
            if (!isInputToActive && !isFeedsFromActive) return;

            // Use different colors based on connection direction
            ctx.strokeStyle = isInputToActive ? connectionColors.inputFrom : connectionColors.feedsInto;
            ctx.lineWidth = 2.5;
            // "Feeds into" (output) connections are dashed, "input from" are solid
            ctx.setLineDash(isFeedsFromActive ? [8, 6] : []);

            ctx.beginPath();
            ctx.moveTo(fromNode.x, fromNode.y);
            ctx.lineTo(toNode.x, toNode.y);
            ctx.stroke();
            ctx.setLineDash([]);
        });
    }, [connectionData, hoveredNode, focusedNode, selectedElement, connectionColors]);

    // Draw nodes
    const drawNodes = useCallback((ctx: CanvasRenderingContext2D) => {
        const zoomScale = config.zoom;

        nodesRef.current.forEach(node => {
            const displayRadius = node.radius * zoomScale * (node.hovered ? 1.15 : 1);

            // Check if this node is connected to the hovered or focused node
            const isConnectedToHovered = hoveredNode && hoveredNode.element !== node.element &&
                connectionData.some(conn =>
                    (conn.from === hoveredNode.element && conn.to === node.element) ||
                    (conn.to === hoveredNode.element && conn.from === node.element)
                );

            const isConnectedToFocused = focusedNode && focusedNode.element !== node.element &&
                connectionData.some(conn =>
                    (conn.from === focusedNode.element && conn.to === node.element) ||
                    (conn.to === focusedNode.element && conn.from === node.element)
                );

            // Check if connected to selected element (panel mode)
            const isConnectedToSelected = selectedElement && selectedElement !== node.element &&
                connectionData.some(conn =>
                    (conn.from === selectedElement && conn.to === node.element) ||
                    (conn.to === selectedElement && conn.from === node.element)
                );

            const isConnected = isConnectedToHovered || isConnectedToFocused || isConnectedToSelected;
            const activeNode = hoveredNode || focusedNode || (selectedElement ? { element: selectedElement } : null);
            const shouldDim = activeNode && activeNode.element !== node.element && !isConnected;
            const opacity = shouldDim ? 0.3 : 1;

            ctx.save();
            ctx.globalAlpha = opacity;

            // Main circle — flat fill
            ctx.fillStyle = node.color;
            ctx.beginPath();
            ctx.arc(node.x, node.y, displayRadius, 0, Math.PI * 2);
            ctx.fill();

            // White border
            ctx.strokeStyle = isConnected
                ? (theme === 'dark' ? 'rgba(147, 197, 253, 1)' : 'rgba(59, 130, 246, 1)')
                : 'rgba(255, 255, 255, 0.8)';
            ctx.lineWidth = node.ring === 0 ? 3 : 2;
            ctx.beginPath();
            ctx.arc(node.x, node.y, displayRadius, 0, Math.PI * 2);
            ctx.stroke();

            // Text inside circle
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';

            if (node.locked) {
                // Draw lock icon for locked elements
                const lockSize = node.ring === 0 ? displayRadius * 0.5 : Math.max(10, displayRadius * 0.4);
                ctx.save();
                ctx.translate(node.x, node.y);
                const scale = lockSize / 512;
                ctx.scale(scale, scale);
                ctx.translate(-512, -512);
                ctx.fillStyle = '#ffffff';
                const lockPath = new Path2D('M832 464h-68V240c0-70.7-57.3-128-128-128H388c-70.7 0-128 57.3-128 128v224h-68c-17.7 0-32 14.3-32 32v384c0 17.7 14.3 32 32 32h640c17.7 0 32-14.3 32-32V496c0-17.7-14.3-32-32-32zM332 240c0-30.9 25.1-56 56-56h248c30.9 0 56 25.1 56 56v224H332V240zm460 600H232V536h560v304zM484 701v53c0 4.4 3.6 8 8 8h40c4.4 0 8-3.6 8-8v-53a48.01 48.01 0 10-56 0z');
                ctx.fill(lockPath);
                ctx.restore();
            } else {
                // Score percentage
                ctx.fillStyle = '#ffffff';
                const fontSize = node.ring === 0 ? displayRadius * 0.5 : Math.max(10, displayRadius * 0.55);
                ctx.font = `700 ${fontSize}px "Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif`;
                ctx.fillText(node.score != null ? `${Math.round(node.score)}%` : 'N/A', node.x, node.y);
            }

            // Label below circle
            ctx.fillStyle = themeColors.textColor;
            const labelSize = node.ring === 0 ? 14 : (node.ring < 4 ? 11 : 9);
            ctx.font = `600 ${Math.round(labelSize * zoomScale)}px "Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif`;

            // Get label offset from config if specified
            const elementConfig = ELEMENT_CONFIG[node.element];
            const labelOffsetX = (elementConfig?.labelOffsetX || 0) * zoomScale;
            const labelOffsetY = (elementConfig?.labelOffsetY || 0) * zoomScale;

            // Word wrap for long labels
            const words = getDisplayName(node.element).split(' ');
            const maxWidth = (node.ring === 0 ? 150 : node.ring < 4 ? 100 : 80) * zoomScale;
            let lines: string[] = [];
            let currentLine = '';

            words.forEach(word => {
                const testLine = currentLine + (currentLine ? ' ' : '') + word;
                const metrics = ctx.measureText(testLine);
                if (metrics.width > maxWidth && currentLine) {
                    lines.push(currentLine);
                    currentLine = word;
                } else {
                    currentLine = testLine;
                }
            });
            if (currentLine) lines.push(currentLine);

            const lineHeight = (node.ring === 0 ? 16 : 12) * zoomScale;
            const startY = node.y + displayRadius + (node.ring === 0 ? 28 : 12) * zoomScale + labelOffsetY;
            lines.forEach((line, i) => {
                ctx.fillText(line, node.x + labelOffsetX, startY + i * lineHeight);
            });

            ctx.restore();
        });
    }, [connectionData, hoveredNode, focusedNode, selectedElement, config.zoom, theme, themeColors.textColor]);

    // Animation loop
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const animate = () => {
            // Clear canvas
            ctx.fillStyle = themeColors.background;
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            // Update nodes
            updateNodes();

            // Draw orbital rings first (behind nodes)
            drawOrbitalRings(ctx);

            // Draw connections (on hover/focus)
            drawConnections(ctx);

            // Draw nodes on top
            drawNodes(ctx);

            animationFrameRef.current = requestAnimationFrame(animate);
        };

        animate();

        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, [updateNodes, drawOrbitalRings, drawConnections, drawNodes, themeColors.background]);

    // Handle mouse move
    const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
        if (pan.isDragging) {
            const deltaX = e.clientX - pan.lastX;
            const deltaY = e.clientY - pan.lastY;
            const hasMoved = pan.hasMoved || Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3;

            if (hasMoved) {
                setPan(prev => ({
                    ...prev,
                    offsetX: prev.offsetX + deltaX,
                    offsetY: prev.offsetY + deltaY,
                    hasMoved: true,
                    lastX: e.clientX,
                    lastY: e.clientY
                }));
            }
            return;
        }

        const canvas = canvasRef.current;
        if (!canvas) return;

        const rect = canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        let foundHover = false;
        let hoveredNodeFound: Node | null = null;

        nodesRef.current = nodesRef.current.map(node => {
            const dx = x - node.x;
            const dy = y - node.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            const hitRadius = node.radius * config.zoom * 1.3;
            const isHovered = distance < hitRadius;

            if (isHovered) {
                foundHover = true;
                hoveredNodeFound = node;
            }

            return { ...node, hovered: isHovered };
        });

        setHoveredNode(hoveredNodeFound);
        canvas.style.cursor = foundHover ? 'pointer' : 'default';
    }, [config.zoom, pan.isDragging, pan.hasMoved, pan.lastX, pan.lastY]);

    // Handle click
    const handleClick = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
        if (pan.hasMoved) return;

        if (hoveredNode) {
            if (viewMode === 'panels' && getElementDetails) {
                // Panel mode - show details in side panels
                const details = getElementDetails(hoveredNode.element);

                // Add current element to history before navigating (if we have a current element)
                if (selectedElement && selectedElementDetails) {
                    const newHistory = [...navigationHistory.slice(0, currentHistoryIndex + 1), { element: selectedElement, details: selectedElementDetails }];
                    setNavigationHistory(newHistory);
                    setCurrentHistoryIndex(newHistory.length);
                } else {
                    // First navigation, start history at index 0
                    setCurrentHistoryIndex(0);
                }

                setSelectedElement(hoveredNode.element);
                setSelectedElementDetails(details);
            } else if (onElementClick) {
                // Drawer mode - use callback
                onElementClick(hoveredNode.element, hoveredNode);
            }
            setHoveredNode(null);
        } else if (viewMode === 'panels' && selectedElement) {
            // Clicked on empty space in panel mode - close panels and reset history
            setSelectedElement(null);
            setSelectedElementDetails(null);
            setNavigationHistory([]);
            setCurrentHistoryIndex(-1);
        }
    }, [hoveredNode, onElementClick, pan.hasMoved, viewMode, getElementDetails, selectedElement, selectedElementDetails, navigationHistory, currentHistoryIndex]);

    // Handle mouse down for pan
    const handleMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
        if (e.button === 0 || e.button === 1) {
            setPan(prev => ({
                ...prev,
                isDragging: true,
                hasMoved: false,
                lastX: e.clientX,
                lastY: e.clientY
            }));
        }
    }, []);

    // Handle mouse up
    const handleMouseUp = useCallback(() => {
        setPan(prev => ({ ...prev, isDragging: false }));
    }, []);

    // Handle mouse leave
    const handleMouseLeave = useCallback(() => {
        setPan(prev => ({ ...prev, isDragging: false }));
        setHoveredNode(null);
    }, []);

    // Zoom controls
    const handleZoomIn = useCallback(() => {
        setConfig(prev => ({ ...prev, zoom: Math.min(MAX_ZOOM, prev.zoom + ZOOM_STEP) }));
    }, []);

    const handleZoomOut = useCallback(() => {
        setConfig(prev => ({ ...prev, zoom: Math.max(MIN_ZOOM, prev.zoom - ZOOM_STEP) }));
    }, []);

    const handleResetView = useCallback(() => {
        setConfig(prev => ({ ...prev, zoom: DEFAULT_ZOOM }));
        setPan({ offsetX: 0, offsetY: 0, isDragging: false, hasMoved: false, lastX: 0, lastY: 0 });
    }, []);

    // Resize canvas
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const parent = canvas.parentElement;
        if (!parent) return;

        const resizeCanvas = () => {
            canvas.width = parent.clientWidth;
            canvas.height = parent.clientHeight;
        };

        resizeCanvas();
        window.addEventListener('resize', resizeCanvas);

        return () => window.removeEventListener('resize', resizeCanvas);
    }, []);

    // Get score color for tooltip
    const getScoreColor = useCallback((elementName: string) => {
        const node = nodesRef.current.find(n => n.element === elementName);
        return node?.color || COLORS.blue;
    }, []);

    // Get element type label based on color group from ELEMENT_CONFIG
    const getElementTypeLabel = useCallback((elementName: string) => {
        const config = ELEMENT_CONFIG[elementName];
        if (config) {
            switch (config.colorGroup) {
                case 'amber': return 'Brand Equity';
                case 'green': return 'Integral';
                case 'orange': return 'Transitional';
                case 'purple': return 'Direct';
                case 'red': return 'Fundamental';
                case 'blue': return 'Measured';
                default: return 'Element';
            }
        }
        // For elements not in config (Quality Components), they're Measured
        return 'Measured';
    }, []);

    // Panel mode: handle clicking on element within panels (navigation)
    // Navigate to element and add to history (used when clicking from within panel)
    const handlePanelElementClick = useCallback((elementName: string) => {
        if (getElementDetails) {
            const details = getElementDetails(elementName);

            // Add current element to history before navigating (if we have a current element)
            if (selectedElement && selectedElementDetails) {
                const newHistory = [...navigationHistory.slice(0, currentHistoryIndex + 1), { element: selectedElement, details: selectedElementDetails }];
                setNavigationHistory(newHistory);
                setCurrentHistoryIndex(newHistory.length);
            } else {
                // First navigation, start history at index 0
                setCurrentHistoryIndex(0);
            }

            setSelectedElement(elementName);
            setSelectedElementDetails(details);
        }
    }, [getElementDetails, selectedElement, selectedElementDetails, navigationHistory, currentHistoryIndex]);

    // Navigate back through history
    const navigateBack = useCallback(() => {
        if (navigationHistory.length > 0 && currentHistoryIndex > 0) {
            const previousEntry = navigationHistory[currentHistoryIndex - 1];
            setCurrentHistoryIndex(currentHistoryIndex - 1);
            setSelectedElement(previousEntry.element);
            setSelectedElementDetails(previousEntry.details);
        }
    }, [navigationHistory, currentHistoryIndex]);

    // Can navigate back if there's history
    const canNavigateBack = navigationHistory.length > 0 && currentHistoryIndex > 0;

    // Panel mode: close panels and reset history
    const handleClosePanel = useCallback(() => {
        setSelectedElement(null);
        setSelectedElementDetails(null);
        setNavigationHistory([]);
        setCurrentHistoryIndex(-1);
    }, []);

    // Panel mode: handle re-analyze
    const handlePanelReanalyze = useCallback(async () => {
        if (selectedElement && onReanalyze) {
            const newSummary = await onReanalyze(selectedElement);
            if (newSummary && selectedElementDetails) {
                setSelectedElementDetails({
                    ...selectedElementDetails,
                    aiSummary: newSummary
                });
            }
        }
    }, [selectedElement, onReanalyze, selectedElementDetails]);

    // Get color for element in panels
    const getPanelElementColor = useCallback((elementName: string) => {
        const node = nodesRef.current.find(n => n.element === elementName);
        return node?.color || COLORS.blue;
    }, []);

    return (
        <div style={{
            width: '100%',
            height: '100%',
            position: 'relative',
            backgroundColor: themeColors.background,
            borderRadius: '8px',
            overflow: 'hidden'
        }}>
            {/* Dark mode scrollbar styles for WebKit browsers */}
            <style>{`
                .v3-dark-scrollbar::-webkit-scrollbar {
                    width: 8px;
                }
                .v3-dark-scrollbar::-webkit-scrollbar-track {
                    background: transparent;
                }
                .v3-dark-scrollbar::-webkit-scrollbar-thumb {
                    background: rgba(100, 116, 139, 0.5);
                    border-radius: 4px;
                }
                .v3-dark-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: rgba(100, 116, 139, 0.7);
                }
            `}</style>
            <canvas
                ref={canvasRef}
                onMouseMove={handleMouseMove}
                onMouseDown={handleMouseDown}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave}
                onClick={handleClick}
                style={{
                    width: '100%',
                    height: '100%',
                    display: 'block',
                    cursor: pan.isDragging ? 'grabbing' : 'default',
                    touchAction: 'none'
                }}
            />

            {/* Controls */}
            <div style={{
                position: 'absolute',
                top: '50px',
                right: '12px',
                zIndex: 100,
                background: theme === 'dark' ? 'rgba(15, 23, 42, 0.95)' : 'rgba(255, 255, 255, 0.98)',
                backdropFilter: 'blur(10px)',
                padding: '12px',
                borderRadius: '8px',
                border: theme === 'dark' ? '1px solid rgba(100, 116, 139, 0.3)' : '1px solid #d9d9d9',
                boxShadow: theme === 'dark' ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.08)',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                minWidth: '150px'
            }}>
                {/* Zoom controls */}
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    <Tooltip title="Zoom out">
                        <Button
                            size="small"
                            icon={<ZoomOutOutlined />}
                            onClick={handleZoomOut}
                            disabled={config.zoom <= MIN_ZOOM}
                        />
                    </Tooltip>
                    <div style={{
                        flex: 1,
                        textAlign: 'center',
                        fontSize: '12px',
                        fontWeight: 500,
                        color: themeColors.subtextColor,
                        minWidth: '40px'
                    }}>
                        {Math.round(config.zoom * 100)}%
                    </div>
                    <Tooltip title="Zoom in">
                        <Button
                            size="small"
                            icon={<ZoomInOutlined />}
                            onClick={handleZoomIn}
                            disabled={config.zoom >= MAX_ZOOM}
                        />
                    </Tooltip>
                    <Tooltip title="Reset view">
                        <Button
                            size="small"
                            icon={<UndoOutlined />}
                            onClick={handleResetView}
                        />
                    </Tooltip>
                </div>
            </div>

            {/* Legend - 5 Color Groups (Vertical) */}
            {false && (
            <div style={{
                position: 'absolute',
                bottom: '20px',
                left: '20px',
                zIndex: 100,
                background: theme === 'dark' ? 'rgba(15, 23, 42, 0.9)' : 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                padding: '12px 14px',
                borderRadius: '12px',
                border: theme === 'dark' ? '1px solid rgba(100, 116, 139, 0.3)' : '1px solid rgba(0, 0, 0, 0.1)'
            }}>
                {/* Element colors */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '10px' }}>
                    <span style={{ fontSize: '10px', color: themeColors.subtextColor, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>Elements:</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: COLORS.green }} />
                        <span style={{ fontSize: '11px', color: themeColors.subtextColor }}>Integral</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: COLORS.orange }} />
                        <span style={{ fontSize: '11px', color: themeColors.subtextColor }}>Transitional</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: COLORS.purple }} />
                        <span style={{ fontSize: '11px', color: themeColors.subtextColor }}>Direct</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: COLORS.red }} />
                        <span style={{ fontSize: '11px', color: themeColors.subtextColor }}>Fundamental</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: COLORS.blue }} />
                        <span style={{ fontSize: '11px', color: themeColors.subtextColor }}>Measured</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: COLORS.grey }} />
                        <span style={{ fontSize: '11px', color: themeColors.subtextColor }}>Locked</span>
                    </div>
                </div>
                {/* Connection colors */}
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                    paddingTop: '10px',
                    borderTop: theme === 'dark' ? '1px solid rgba(100, 116, 139, 0.3)' : '1px solid rgba(0, 0, 0, 0.1)'
                }}>
                    <span style={{ fontSize: '10px', color: themeColors.subtextColor, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>Links:</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '16px', height: '3px', borderRadius: '2px', background: theme === 'dark' ? '#5a8ab0' : '#3d5a80' }} />
                        <span style={{ fontSize: '11px', color: themeColors.subtextColor }}>Input from</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '16px', height: '3px', borderRadius: '2px', background: theme === 'dark' ? '#e8967e' : '#e07a5f', borderStyle: 'dashed' }} />
                        <span style={{ fontSize: '11px', color: themeColors.subtextColor }}>Output to</span>
                    </div>
                </div>
            </div>
            )}

            {/* Instructions */}
            <div style={{
                position: 'absolute',
                bottom: '20px',
                right: '20px',
                zIndex: 100,
                background: theme === 'dark' ? 'rgba(15, 23, 42, 0.9)' : 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                padding: '12px 16px',
                borderRadius: '12px',
                border: theme === 'dark' ? '1px solid rgba(100, 116, 139, 0.3)' : '1px solid rgba(0, 0, 0, 0.1)',
                fontSize: '12px',
                color: themeColors.subtextColor,
                lineHeight: 1.5
            }}>
                <strong style={{ color: '#60a5fa' }}>Click</strong> to view details &bull;
                <strong style={{ color: '#60a5fa' }}> Hover</strong> to highlight &bull;
                <strong style={{ color: '#60a5fa' }}> Drag</strong> to pan
            </div>

            {/* Panel Mode - Side Panels */}
            {viewMode === 'panels' && selectedElement && selectedElementDetails && (
                <>
                    {/* Right Panel - Input from & Output to */}
                    {!selectedElementDetails.locked && (
                        <div
                            className={theme === 'dark' ? 'v3-dark-scrollbar' : ''}
                            style={{
                            position: 'absolute',
                            top: '20px',
                            right: '20px',
                            width: '260px',
                            maxHeight: 'calc(100% - 300px)',
                            overflowY: 'auto',
                            zIndex: 100,
                            background: theme === 'dark' ? 'rgba(15, 23, 42, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                            backdropFilter: 'blur(10px)',
                            padding: '16px',
                            borderRadius: '12px',
                            border: theme === 'dark' ? '1px solid rgba(100, 116, 139, 0.3)' : '1px solid rgba(0, 0, 0, 0.1)',
                            boxShadow: theme === 'dark' ? '0 4px 12px rgba(0, 0, 0, 0.4)' : '0 4px 12px rgba(0, 0, 0, 0.15)',
                            scrollbarWidth: 'thin',
                            scrollbarColor: theme === 'dark' ? 'rgba(100, 116, 139, 0.5) transparent' : undefined
                        }}>
                            {/* Input Elements */}
                            {selectedElementDetails.inputs && selectedElementDetails.inputs.length > 0 && (
                                <div style={{ marginBottom: '16px' }}>
                                    <div style={{
                                        fontSize: '11px',
                                        fontWeight: '600',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        color: theme === 'dark' ? '#5a8ab0' : '#3d5a80',
                                        marginBottom: '10px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px'
                                    }}>
                                        <div style={{ width: '12px', height: '3px', background: theme === 'dark' ? '#5a8ab0' : '#3d5a80', borderRadius: '2px' }} />
                                        Input from ({selectedElementDetails.inputs.length})
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                        {selectedElementDetails.inputs.map((input, index) => {
                                            const inputLocked = isElementLocked(input.name);
                                            const isPriceDeals = input.name === 'Price Deals';
                                            // For Price Deals: -100 to 0 scale, for others: -100 to +100 scale
                                            const indicatorPosition = input.score != null
                                                ? (isPriceDeals
                                                    ? ((input.score + 100) / 100) * 100
                                                    : ((input.score + 100) / 200) * 100)
                                                : 50;
                                            const zeroPosition = isPriceDeals ? 100 : 50;
                                            const displayScore = input.score != null
                                                ? (input.score > 0 ? `+${input.score.toFixed(0)}%` : `${input.score.toFixed(0)}%`)
                                                : 'N/A';

                                            return (
                                                <div
                                                    key={index}
                                                    onClick={() => handlePanelElementClick(input.name)}
                                                    style={{
                                                        padding: '10px 12px',
                                                        backgroundColor: inputLocked
                                                            ? (theme === 'dark' ? 'rgba(156, 163, 175, 0.15)' : 'rgba(156, 163, 175, 0.1)')
                                                            : (theme === 'dark' ? 'rgba(61, 90, 128, 0.1)' : 'rgba(61, 90, 128, 0.06)'),
                                                        borderRadius: '6px',
                                                        border: inputLocked
                                                            ? (theme === 'dark' ? '1px solid rgba(156, 163, 175, 0.3)' : '1px solid #d1d5db')
                                                            : (theme === 'dark' ? '1px solid rgba(61, 90, 128, 0.2)' : '1px solid rgba(61, 90, 128, 0.15)'),
                                                        cursor: 'pointer',
                                                        transition: 'all 0.2s'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.transform = 'translateX(2px)';
                                                        e.currentTarget.style.borderColor = inputLocked
                                                            ? (theme === 'dark' ? 'rgba(156, 163, 175, 0.5)' : '#9ca3af')
                                                            : (theme === 'dark' ? 'rgba(61, 90, 128, 0.4)' : 'rgba(61, 90, 128, 0.3)');
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.transform = 'translateX(0)';
                                                        e.currentTarget.style.borderColor = inputLocked
                                                            ? (theme === 'dark' ? 'rgba(156, 163, 175, 0.3)' : '#d1d5db')
                                                            : (theme === 'dark' ? 'rgba(61, 90, 128, 0.2)' : 'rgba(61, 90, 128, 0.15)');
                                                    }}
                                                >
                                                    <div style={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        marginBottom: inputLocked ? '0' : '6px'
                                                    }}>
                                                        <span style={{
                                                            fontSize: '12px',
                                                            fontWeight: '500',
                                                            color: inputLocked ? '#9ca3af' : themeColors.textColor
                                                        }}>
                                                            {getDisplayName(input.name)}
                                                        </span>
                                                        {inputLocked ? (
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#9ca3af', fontSize: '11px' }}>
                                                                <LockOutlined style={{ fontSize: '12px' }} />
                                                                <span>Locked</span>
                                                            </div>
                                                        ) : (
                                                            <span style={{
                                                                fontSize: '11px',
                                                                fontWeight: '600',
                                                                color: getPanelElementColor(input.name)
                                                            }}>
                                                                {displayScore}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {/* Progress indicator - only show if not locked */}
                                                    {!inputLocked && input.score != null && (
                                                        <div style={{
                                                            position: 'relative',
                                                            height: '14px',
                                                            display: 'flex',
                                                            alignItems: 'center'
                                                        }}>
                                                            <div style={{
                                                                position: 'relative',
                                                                width: '100%',
                                                                height: '6px',
                                                                background: isPriceDeals
                                                                    ? 'linear-gradient(to right, #ef4444 0%, #f59e0b 30%, #eab308 60%, #84cc16 85%, #22c55e 100%)'
                                                                    : 'linear-gradient(to right, #ef4444 0%, #f59e0b 25%, #eab308 40%, #84cc16 60%, #22c55e 75%, #10b981 100%)',
                                                                borderRadius: '3px',
                                                                border: theme === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
                                                                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.1)'
                                                            }}>
                                                                {/* Zero marker */}
                                                                <div style={{
                                                                    position: 'absolute',
                                                                    left: `${zeroPosition}%`,
                                                                    top: '-1px',
                                                                    bottom: '-1px',
                                                                    width: '1px',
                                                                    backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                                                                    transform: 'translateX(-50%)'
                                                                }} />
                                                            </div>
                                                            {/* Position indicator - vertical bar */}
                                                            <div style={{
                                                                position: 'absolute',
                                                                left: `${indicatorPosition}%`,
                                                                top: '0',
                                                                width: '3px',
                                                                height: '14px',
                                                                backgroundColor: theme === 'dark' ? '#fff' : '#1f2937',
                                                                borderRadius: '2px',
                                                                transform: 'translateX(-50%)',
                                                                boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                                                            }} />
                                                        </div>
                                                    )}
                                                    {/* Price Deals note - only show if not locked */}
                                                    {!inputLocked && isPriceDeals && (
                                                        <div style={{
                                                            fontSize: '9px',
                                                            color: themeColors.subtextColor,
                                                            fontStyle: 'italic',
                                                            marginTop: '4px'
                                                        }}>
                                                            Scale: -100% to 0%
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Output Elements */}
                            {selectedElementDetails.outputs && selectedElementDetails.outputs.length > 0 && (
                                <div>
                                    <div style={{
                                        fontSize: '11px',
                                        fontWeight: '600',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        color: theme === 'dark' ? '#e8967e' : '#e07a5f',
                                        marginBottom: '10px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px'
                                    }}>
                                        <div style={{ width: '12px', height: '3px', background: theme === 'dark' ? '#e8967e' : '#e07a5f', borderRadius: '2px' }} />
                                        Output to ({selectedElementDetails.outputs.length})
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                        {selectedElementDetails.outputs.map((output, index) => {
                                            const outputLocked = isElementLocked(output.name);
                                            const isPriceDeals = output.name === 'Price Deals';
                                            // For Price Deals: -100 to 0 scale, for others: -100 to +100 scale
                                            const indicatorPosition = output.score != null
                                                ? (isPriceDeals
                                                    ? ((output.score + 100) / 100) * 100
                                                    : ((output.score + 100) / 200) * 100)
                                                : 50;
                                            const zeroPosition = isPriceDeals ? 100 : 50;
                                            const displayScore = output.score != null
                                                ? (output.score > 0 ? `+${output.score.toFixed(0)}%` : `${output.score.toFixed(0)}%`)
                                                : 'N/A';

                                            return (
                                                <div
                                                    key={index}
                                                    onClick={() => handlePanelElementClick(output.name)}
                                                    style={{
                                                        padding: '10px 12px',
                                                        backgroundColor: outputLocked
                                                            ? (theme === 'dark' ? 'rgba(156, 163, 175, 0.15)' : 'rgba(156, 163, 175, 0.1)')
                                                            : (theme === 'dark' ? 'rgba(224, 122, 95, 0.1)' : 'rgba(224, 122, 95, 0.06)'),
                                                        borderRadius: '6px',
                                                        border: outputLocked
                                                            ? (theme === 'dark' ? '1px solid rgba(156, 163, 175, 0.3)' : '1px solid #d1d5db')
                                                            : (theme === 'dark' ? '1px solid rgba(224, 122, 95, 0.2)' : '1px solid rgba(224, 122, 95, 0.15)'),
                                                        cursor: 'pointer',
                                                        transition: 'all 0.2s'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.transform = 'translateX(2px)';
                                                        e.currentTarget.style.borderColor = outputLocked
                                                            ? (theme === 'dark' ? 'rgba(156, 163, 175, 0.5)' : '#9ca3af')
                                                            : (theme === 'dark' ? 'rgba(224, 122, 95, 0.4)' : 'rgba(224, 122, 95, 0.3)');
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.transform = 'translateX(0)';
                                                        e.currentTarget.style.borderColor = outputLocked
                                                            ? (theme === 'dark' ? 'rgba(156, 163, 175, 0.3)' : '#d1d5db')
                                                            : (theme === 'dark' ? 'rgba(224, 122, 95, 0.2)' : 'rgba(224, 122, 95, 0.15)');
                                                    }}
                                                >
                                                    <div style={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        marginBottom: outputLocked ? '0' : '6px'
                                                    }}>
                                                        <span style={{
                                                            fontSize: '12px',
                                                            fontWeight: '500',
                                                            color: outputLocked ? '#9ca3af' : themeColors.textColor
                                                        }}>
                                                            {getDisplayName(output.name)}
                                                        </span>
                                                        {outputLocked ? (
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#9ca3af', fontSize: '11px' }}>
                                                                <LockOutlined style={{ fontSize: '12px' }} />
                                                                <span>Locked</span>
                                                            </div>
                                                        ) : (
                                                            <span style={{
                                                                fontSize: '11px',
                                                                fontWeight: '600',
                                                                color: getPanelElementColor(output.name)
                                                            }}>
                                                                {displayScore}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {/* Progress indicator - only show if not locked */}
                                                    {!outputLocked && output.score != null && (
                                                        <div style={{
                                                            position: 'relative',
                                                            height: '14px',
                                                            display: 'flex',
                                                            alignItems: 'center'
                                                        }}>
                                                            <div style={{
                                                                position: 'relative',
                                                                width: '100%',
                                                                height: '6px',
                                                                background: isPriceDeals
                                                                    ? 'linear-gradient(to right, #ef4444 0%, #f59e0b 30%, #eab308 60%, #84cc16 85%, #22c55e 100%)'
                                                                    : 'linear-gradient(to right, #ef4444 0%, #f59e0b 25%, #eab308 40%, #84cc16 60%, #22c55e 75%, #10b981 100%)',
                                                                borderRadius: '3px',
                                                                border: theme === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
                                                                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.1)'
                                                            }}>
                                                                {/* Zero marker */}
                                                                <div style={{
                                                                    position: 'absolute',
                                                                    left: `${zeroPosition}%`,
                                                                    top: '-1px',
                                                                    bottom: '-1px',
                                                                    width: '1px',
                                                                    backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                                                                    transform: 'translateX(-50%)'
                                                                }} />
                                                            </div>
                                                            {/* Position indicator - vertical bar */}
                                                            <div style={{
                                                                position: 'absolute',
                                                                left: `${indicatorPosition}%`,
                                                                top: '0',
                                                                width: '3px',
                                                                height: '14px',
                                                                backgroundColor: theme === 'dark' ? '#fff' : '#1f2937',
                                                                borderRadius: '2px',
                                                                transform: 'translateX(-50%)',
                                                                boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                                                            }} />
                                                        </div>
                                                    )}
                                                    {/* Price Deals note - only show if not locked */}
                                                    {!outputLocked && isPriceDeals && (
                                                        <div style={{
                                                            fontSize: '9px',
                                                            color: themeColors.subtextColor,
                                                            fontStyle: 'italic',
                                                            marginTop: '4px'
                                                        }}>
                                                            Scale: -100% to 0%
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Survey Statements for Measured Elements */}
                            {selectedElementDetails.type === 'qc' && selectedElementDetails.statements && selectedElementDetails.statements.length > 0 && (
                                <div style={{ marginTop: '16px' }}>
                                    <div style={{
                                        fontSize: '11px',
                                        fontWeight: '600',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        color: theme === 'dark' ? '#60a5fa' : '#2563eb',
                                        marginBottom: '10px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px'
                                    }}>
                                        <div style={{ width: '12px', height: '3px', background: theme === 'dark' ? '#60a5fa' : '#2563eb', borderRadius: '2px' }} />
                                        Survey Statements ({selectedElementDetails.statements.length})
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                        {selectedElementDetails.statements.map((statement, index) => {
                                            const isLowResponse = statement.responseCount < 20;
                                            return (
                                                <div
                                                    key={statement.id}
                                                    onClick={() => onStatementClick?.(statement, selectedElement!)}
                                                    style={{
                                                        padding: '10px 12px',
                                                        backgroundColor: isLowResponse
                                                            ? (theme === 'dark' ? 'rgba(251, 191, 36, 0.15)' : 'rgba(251, 191, 36, 0.2)')
                                                            : (theme === 'dark' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.08)'),
                                                        borderRadius: '6px',
                                                        border: isLowResponse
                                                            ? (theme === 'dark' ? '1px solid rgba(251, 191, 36, 0.3)' : '1px solid rgba(251, 191, 36, 0.5)')
                                                            : (theme === 'dark' ? '1px solid rgba(59, 130, 246, 0.2)' : '1px solid rgba(59, 130, 246, 0.15)'),
                                                        cursor: 'pointer',
                                                        transition: 'all 0.2s'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.transform = 'translateX(2px)';
                                                        e.currentTarget.style.borderColor = isLowResponse
                                                            ? (theme === 'dark' ? 'rgba(251, 191, 36, 0.5)' : 'rgba(251, 191, 36, 0.7)')
                                                            : (theme === 'dark' ? 'rgba(59, 130, 246, 0.4)' : 'rgba(59, 130, 246, 0.3)');
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.transform = 'translateX(0)';
                                                        e.currentTarget.style.borderColor = isLowResponse
                                                            ? (theme === 'dark' ? 'rgba(251, 191, 36, 0.3)' : 'rgba(251, 191, 36, 0.5)')
                                                            : (theme === 'dark' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.15)');
                                                    }}
                                                >
                                                    <div style={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'flex-start',
                                                        marginBottom: '6px'
                                                    }}>
                                                        <span style={{
                                                            fontSize: '10px',
                                                            fontWeight: '500',
                                                            color: themeColors.subtextColor
                                                        }}>
                                                            Statement {index + 1}
                                                        </span>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                            {isLowResponse && (
                                                                <span style={{
                                                                    fontSize: '9px',
                                                                    fontWeight: '600',
                                                                    color: '#f59e0b',
                                                                    backgroundColor: theme === 'dark' ? 'rgba(245, 158, 11, 0.2)' : 'rgba(245, 158, 11, 0.15)',
                                                                    padding: '2px 5px',
                                                                    borderRadius: '3px'
                                                                }}>
                                                                    LOW
                                                                </span>
                                                            )}
                                                            <span style={{
                                                                fontSize: '10px',
                                                                color: isLowResponse ? '#f59e0b' : themeColors.subtextColor,
                                                                fontWeight: isLowResponse ? '600' : '400'
                                                            }}>
                                                                {statement.responseCount} resp.
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div style={{
                                                        fontSize: '11px',
                                                        color: themeColors.textColor,
                                                        lineHeight: '1.4',
                                                        marginBottom: '8px'
                                                    }}>
                                                        {statement.text.length > 80 ? statement.text.substring(0, 80) + '...' : statement.text}
                                                    </div>
                                                    {/* Progress indicator - matches drawer StrengthSlider */}
                                                    {(() => {
                                                        // Statement scores use -100 to +100 scale, map to 0-100 for position
                                                        const meanScore = statement.meanScore ?? 0;
                                                        const indicatorPosition = ((meanScore + 100) / 200) * 100;
                                                        const displayScore = statement.meanScore != null
                                                            ? (meanScore > 0 ? `+${meanScore.toFixed(0)}%` : `${meanScore.toFixed(0)}%`)
                                                            : 'N/A';

                                                        return (
                                                            <div style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '8px'
                                                            }}>
                                                                <div style={{
                                                                    flex: 1,
                                                                    position: 'relative',
                                                                    height: '16px',
                                                                    display: 'flex',
                                                                    alignItems: 'center'
                                                                }}>
                                                                    <div style={{
                                                                        position: 'relative',
                                                                        width: '100%',
                                                                        height: '12px',
                                                                        background: 'linear-gradient(to right, #ef4444, #eab308, #22c55e)',
                                                                        borderRadius: '4px',
                                                                        border: theme === 'dark' ? '1px solid rgba(255,255,255,0.2)' : '1px solid #374151'
                                                                    }}>
                                                                        {/* Center line at 50% */}
                                                                        <div style={{
                                                                            position: 'absolute',
                                                                            left: '50%',
                                                                            top: '-2px',
                                                                            bottom: '-2px',
                                                                            width: '2px',
                                                                            backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.3)',
                                                                            transform: 'translateX(-50%)'
                                                                        }} />
                                                                    </div>
                                                                    {/* Position indicator - vertical bar */}
                                                                    <div style={{
                                                                        position: 'absolute',
                                                                        left: `${indicatorPosition}%`,
                                                                        top: '0',
                                                                        width: '4px',
                                                                        height: '16px',
                                                                        backgroundColor: theme === 'dark' ? '#fff' : '#000',
                                                                        borderRadius: '2px',
                                                                        transform: 'translateX(-50%)',
                                                                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                                                    }} />
                                                                </div>
                                                                <span style={{
                                                                    fontSize: '11px',
                                                                    fontWeight: '600',
                                                                    color: themeColors.textColor,
                                                                    minWidth: '42px',
                                                                    textAlign: 'right'
                                                                }}>
                                                                    {displayScore}
                                                                </span>
                                                            </div>
                                                        );
                                                    })()}
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {/* Response Count Gauge — smaller */}
                                    {(() => {
                                        const totalResponses = selectedElementDetails.statements.reduce((sum, s) => sum + s.responseCount, 0);

                                        // Status driven by total responses, same 20/40 thresholds (max scale 60)
                                        const maxScale = 60;
                                        const percentage = Math.min((totalResponses / maxScale) * 100, 100);
                                        const getStatus = () => {
                                            if (totalResponses <= 20) return { text: 'Low', color: '#ef4444' };
                                            if (totalResponses <= 40) return { text: 'Moderate', color: '#eab308' };
                                            return { text: 'Good', color: '#22c55e' };
                                        };
                                        const status = getStatus();

                                        return (
                                            <div style={{
                                                marginTop: '12px',
                                                padding: '8px 10px',
                                                backgroundColor: theme === 'dark' ? 'rgba(100, 116, 139, 0.15)' : '#f9fafb',
                                                borderRadius: '6px',
                                                border: theme === 'dark' ? '1px solid rgba(100, 116, 139, 0.3)' : '1px solid #e5e7eb'
                                            }}>
                                                {/* Header */}
                                                <div style={{
                                                    fontSize: '9px',
                                                    color: themeColors.subtextColor,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.3px',
                                                    marginBottom: '6px',
                                                    textAlign: 'center'
                                                }}>
                                                    Total Responses
                                                </div>

                                                {/* Gauge bar */}
                                                <div style={{
                                                    position: 'relative',
                                                    height: '12px',
                                                    marginBottom: '4px'
                                                }}>
                                                    <div style={{
                                                        position: 'absolute',
                                                        top: '3px',
                                                        left: 0,
                                                        right: 0,
                                                        height: '6px',
                                                        background: 'linear-gradient(to right, #ef4444 0%, #f97316 33%, #eab308 50%, #84cc16 66%, #22c55e 100%)',
                                                        borderRadius: '3px',
                                                        opacity: 0.3,
                                                        border: theme === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)'
                                                    }} />
                                                    <div style={{
                                                        position: 'absolute',
                                                        top: '3px',
                                                        left: 0,
                                                        width: `${percentage}%`,
                                                        height: '6px',
                                                        background: 'linear-gradient(to right, #ef4444 0%, #f97316 33%, #eab308 50%, #84cc16 66%, #22c55e 100%)',
                                                        backgroundSize: `${100 / (percentage / 100)}% 100%`,
                                                        borderRadius: '3px',
                                                        transition: 'width 0.3s ease'
                                                    }} />
                                                    <div style={{
                                                        position: 'absolute',
                                                        left: `${percentage}%`,
                                                        top: '0',
                                                        width: '3px',
                                                        height: '12px',
                                                        backgroundColor: theme === 'dark' ? '#fff' : '#1f2937',
                                                        borderRadius: '2px',
                                                        transform: 'translateX(-50%)',
                                                        boxShadow: '0 1px 2px rgba(0,0,0,0.3)'
                                                    }} />
                                                </div>

                                                {/* Scale labels */}
                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    fontSize: '8px',
                                                    color: themeColors.subtextColor,
                                                    marginBottom: '6px'
                                                }}>
                                                    <span>Low</span>
                                                    <span>Moderate</span>
                                                    <span>Good</span>
                                                </div>

                                                {/* Total count and status */}
                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'baseline',
                                                    gap: '6px'
                                                }}>
                                                    <span style={{
                                                        fontSize: '16px',
                                                        fontWeight: 'bold',
                                                        color: themeColors.textColor
                                                    }}>
                                                        {totalResponses}
                                                    </span>
                                                    <span style={{
                                                        fontSize: '10px',
                                                        fontWeight: '600',
                                                        color: status.color
                                                    }}>
                                                        {status.text}
                                                    </span>
                                                </div>

                                                {totalResponses <= 20 && (
                                                    <div style={{
                                                        marginTop: '4px',
                                                        fontSize: '9px',
                                                        color: '#ef4444',
                                                        textAlign: 'center',
                                                        fontStyle: 'italic'
                                                    }}>
                                                        Results may not be statistically reliable
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })()}
                                </div>
                            )}

                            {/* No connections message */}
                            {(!selectedElementDetails.inputs || selectedElementDetails.inputs.length === 0) &&
                             (!selectedElementDetails.outputs || selectedElementDetails.outputs.length === 0) &&
                             (selectedElementDetails.type !== 'qc' || !selectedElementDetails.statements || selectedElementDetails.statements.length === 0) && (
                                <div style={{
                                    textAlign: 'center',
                                    color: themeColors.subtextColor,
                                    fontSize: '12px',
                                    padding: '20px'
                                }}>
                                    No connections for this element.
                                </div>
                            )}
                        </div>
                    )}

                    {/* Left Panel - Element Info & AI Summary (positioned at top, inline with right panel) */}
                    <div
                        className={theme === 'dark' ? 'v3-dark-scrollbar' : ''}
                        style={{
                        position: 'absolute',
                        top: '20px',
                        left: '20px',
                        width: '280px',
                        maxHeight: 'calc(100% - 40px)',
                        overflowY: 'auto',
                        zIndex: 100,
                        background: theme === 'dark' ? 'rgba(15, 23, 42, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(10px)',
                        padding: '16px',
                        borderRadius: '12px',
                        border: theme === 'dark' ? '1px solid rgba(100, 116, 139, 0.3)' : '1px solid rgba(0, 0, 0, 0.1)',
                        boxShadow: theme === 'dark' ? '0 4px 12px rgba(0, 0, 0, 0.4)' : '0 4px 12px rgba(0, 0, 0, 0.15)',
                        scrollbarWidth: 'thin',
                        scrollbarColor: theme === 'dark' ? 'rgba(100, 116, 139, 0.5) transparent' : undefined
                    }}>
                        {/* Back button (when history exists) */}
                        {canNavigateBack && (
                            <button
                                onClick={navigateBack}
                                style={{
                                    position: 'absolute',
                                    top: '12px',
                                    left: '12px',
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '4px',
                                    color: themeColors.subtextColor,
                                    fontSize: '14px'
                                }}
                            >
                                <LeftOutlined />
                            </button>
                        )}

                        {/* Close button */}
                        <button
                            onClick={handleClosePanel}
                            style={{
                                position: 'absolute',
                                top: '12px',
                                right: '12px',
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                padding: '4px',
                                color: themeColors.subtextColor,
                                fontSize: '14px'
                            }}
                        >
                            <CloseOutlined />
                        </button>

                        {/* Element Name & Score */}
                        <div style={{ marginBottom: '16px' }}>
                            <div style={{
                                fontSize: '18px',
                                fontWeight: 'bold',
                                color: themeColors.textColor,
                                marginBottom: '8px',
                                paddingRight: '24px',
                                paddingLeft: canNavigateBack ? '24px' : '0'
                            }}>
                                {getDisplayName(selectedElement)}
                            </div>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}>
                                {selectedElementDetails.locked ? (
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        color: '#9ca3af'
                                    }}>
                                        <LockOutlined style={{ fontSize: '20px' }} />
                                        <span>Locked</span>
                                    </div>
                                ) : (
                                    <>
                                        <span style={{
                                            fontSize: '32px',
                                            fontWeight: 'bold',
                                            color: getPanelElementColor(selectedElement)
                                        }}>
                                            {selectedElementDetails.score != null ? `${selectedElementDetails.score.toFixed(1)}%` : 'N/A'}
                                        </span>
                                        <span style={{
                                            fontSize: '12px',
                                            color: themeColors.subtextColor,
                                            textTransform: 'uppercase'
                                        }}>
                                            {selectedElementDetails.classificationLabel}
                                        </span>
                                    </>
                                )}
                            </div>
                            {/* Score Progress Indicator */}
                            {!selectedElementDetails.locked && selectedElementDetails.score != null && (() => {
                                const isPriceDeals = selectedElement === 'Price Deals';
                                const score = selectedElementDetails.score;
                                const indicatorPosition = isPriceDeals
                                    ? ((score + 100) / 100) * 100
                                    : ((score + 100) / 200) * 100;
                                const zeroPosition = isPriceDeals ? 100 : 50;

                                return (
                                    <div style={{ marginTop: '8px' }}>
                                        <div style={{
                                            position: 'relative',
                                            height: '16px',
                                            display: 'flex',
                                            alignItems: 'center'
                                        }}>
                                            <div style={{
                                                position: 'relative',
                                                width: '100%',
                                                height: '8px',
                                                background: isPriceDeals
                                                    ? 'linear-gradient(to right, #ef4444 0%, #f59e0b 30%, #eab308 60%, #84cc16 85%, #22c55e 100%)'
                                                    : 'linear-gradient(to right, #ef4444 0%, #f59e0b 25%, #eab308 40%, #84cc16 60%, #22c55e 75%, #10b981 100%)',
                                                borderRadius: '4px',
                                                border: theme === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
                                                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.1)'
                                            }}>
                                                {/* Zero marker */}
                                                <div style={{
                                                    position: 'absolute',
                                                    left: `${zeroPosition}%`,
                                                    top: '-1px',
                                                    bottom: '-1px',
                                                    width: '1px',
                                                    backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                                                    transform: 'translateX(-50%)'
                                                }} />
                                            </div>
                                            {/* Position indicator - vertical bar */}
                                            <div style={{
                                                position: 'absolute',
                                                left: `${indicatorPosition}%`,
                                                top: '0',
                                                width: '3px',
                                                height: '16px',
                                                backgroundColor: theme === 'dark' ? '#fff' : '#1f2937',
                                                borderRadius: '2px',
                                                transform: 'translateX(-50%)',
                                                boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                                            }} />
                                        </div>
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            fontSize: '10px',
                                            color: themeColors.subtextColor,
                                            marginTop: '4px'
                                        }}>
                                            <span>-100%</span>
                                            <span>{isPriceDeals ? '0%' : '100%'}</span>
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>

                        {/* Element Description */}
                        {!selectedElementDetails.locked && elementDescriptions[selectedElement]?.sidebar && (
                            <div style={{
                                fontSize: '13px',
                                color: themeColors.subtextColor,
                                lineHeight: '1.5',
                                marginBottom: '16px'
                            }}>
                                {elementDescriptions[selectedElement].sidebar}
                            </div>
                        )}

                        {/* AI Summary */}
                        {!selectedElementDetails.locked && (
                            <div style={{
                                padding: '12px',
                                backgroundColor: theme === 'dark' ? 'rgba(59, 130, 246, 0.1)' : '#f8fafc',
                                borderRadius: '8px',
                                border: theme === 'dark' ? '1px solid rgba(59, 130, 246, 0.3)' : '1px solid #e2e8f0',
                                borderLeft: '4px solid #3b82f6'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    marginBottom: '8px'
                                }}>
                                    <span style={{ fontSize: '14px', marginRight: '6px' }}>💭</span>
                                    <span style={{
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        color: '#3b82f6'
                                    }}>
                                        First Thoughts
                                    </span>
                                </div>
                                <div style={{
                                    color: themeColors.subtextColor,
                                    fontSize: '13px',
                                    lineHeight: '1.5'
                                }}>
                                    <ReactMarkdown
                                        components={{
                                            p: ({children}) => <span style={{ display: 'block', marginBottom: '6px' }}>{children}</span>,
                                            strong: ({children}) => <strong style={{ fontWeight: 600, color: themeColors.textColor }}>{children}</strong>,
                                            em: ({children}) => <em style={{ fontStyle: 'italic' }}>{children}</em>,
                                            ul: ({children}) => <ul style={{ paddingLeft: '14px', marginBottom: '6px' }}>{children}</ul>,
                                            li: ({children}) => <li style={{ marginBottom: '2px' }}>{children}</li>
                                        }}
                                    >
                                        {selectedElementDetails.aiSummary || intl.formatMessage({ id: 'brandEquity.aiSummary.awaitingResponses' })}
                                    </ReactMarkdown>
                                </div>
                                <button
                                    onClick={handlePanelReanalyze}
                                    disabled={isReanalyzing}
                                    style={{
                                        marginTop: '10px',
                                        padding: '6px 12px',
                                        fontSize: '11px',
                                        fontWeight: '600',
                                        cursor: isReanalyzing ? 'not-allowed' : 'pointer',
                                        background: theme === 'dark' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.1)',
                                        color: '#60a5fa',
                                        border: '1px solid rgba(59, 130, 246, 0.3)',
                                        borderRadius: '6px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        opacity: isReanalyzing ? 0.6 : 1
                                    }}
                                >
                                    <ReloadOutlined spin={isReanalyzing} style={{ fontSize: '12px' }} />
                                    {isReanalyzing ? 'Analysing...' : 'Analyse Again'}
                                </button>
                            </div>
                        )}

                        {/* Locked message */}
                        {selectedElementDetails.locked && (
                            <div style={{
                                padding: '20px 16px',
                                textAlign: 'center',
                                color: themeColors.subtextColor
                            }}>
                                <div style={{
                                    width: '60px',
                                    height: '60px',
                                    borderRadius: '50%',
                                    backgroundColor: theme === 'dark' ? 'rgba(156, 163, 175, 0.15)' : 'rgba(156, 163, 175, 0.2)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 16px'
                                }}>
                                    <LockOutlined style={{ fontSize: '28px', color: '#9ca3af' }} />
                                </div>
                                <div style={{ fontSize: '14px', fontWeight: 600, color: themeColors.textColor, marginBottom: '8px' }}>
                                    Premium Feature
                                </div>
                                <div style={{ fontSize: '13px', marginBottom: '16px', lineHeight: 1.5 }}>
                                    Upgrade to unlock detailed insights for <strong style={{ color: themeColors.textColor }}>{getDisplayName(selectedElement)}</strong>
                                </div>
                                {onUpgradeClick && (
                                    <button
                                        onClick={onUpgradeClick}
                                        style={{
                                            padding: '10px 20px',
                                            borderRadius: '8px',
                                            fontSize: '13px',
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                            background: 'linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)',
                                            color: '#ffffff',
                                            border: 'none',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            boxShadow: '0 2px 8px rgba(245, 158, 11, 0.3)'
                                        }}
                                    >
                                        <CrownOutlined style={{ fontSize: '14px' }} />
                                        Upgrade to Unlock
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* Tooltip - only shown when no element is selected */}
            {!selectedElement && <div style={{
                position: 'absolute',
                top: '20px',
                left: '20px',
                width: '260px',
                padding: '16px',
                background: theme === 'dark' ? 'rgba(15, 23, 42, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                borderRadius: '12px',
                fontSize: '14px',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                pointerEvents: 'none',
                zIndex: 1000,
                boxShadow: theme === 'dark' ? '0 4px 12px rgba(0, 0, 0, 0.4)' : '0 4px 12px rgba(0, 0, 0, 0.1)',
                border: theme === 'dark' ? '1px solid rgba(100, 116, 139, 0.3)' : '1px solid rgba(0, 0, 0, 0.1)',
                opacity: hoveredNode ? 1 : 0,
                transition: 'opacity 0.2s'
            }}>
                {hoveredNode && (
                    <>
                        <div style={{
                            fontWeight: 'bold',
                            marginBottom: '8px',
                            fontSize: '16px',
                            color: themeColors.textColor
                        }}>
                            {getDisplayName(hoveredNode.element)}
                        </div>
                        {elementDescriptions[hoveredNode.element]?.mouseover && (
                            <div style={{
                                marginBottom: '8px',
                                fontSize: '12px',
                                color: themeColors.subtextColor,
                                fontStyle: 'italic'
                            }}>
                                {elementDescriptions[hoveredNode.element].mouseover}
                            </div>
                        )}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <span style={{ color: themeColors.subtextColor }}>Score:</span>
                            {hoveredNode.locked ? (
                                <LockOutlined style={{ fontSize: '28px', color: '#9ca3af' }} />
                            ) : (
                                <span style={{
                                    color: getScoreColor(hoveredNode.element),
                                    fontWeight: 'bold',
                                    fontSize: '24px'
                                }}>
                                    {hoveredNode.score != null ? `${hoveredNode.score.toFixed(1)}%` : 'N/A'}
                                </span>
                            )}
                        </div>
                        <div style={{
                            marginTop: '8px',
                            paddingTop: '8px',
                            borderTop: theme === 'dark' ? '1px solid rgba(100, 116, 139, 0.3)' : '1px solid rgba(0, 0, 0, 0.1)',
                            fontSize: '12px',
                            color: themeColors.subtextColor
                        }}>
                            {getElementTypeLabel(hoveredNode.element)}
                        </div>
                    </>
                )}
            </div>}
        </div>
    );
};

export default BrandEquityVisualizationV3;
